import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { MODULES } from "@/data/constants";
import { MODULE_IMAGES } from "@/data/moduleMedia";
import { ModuleIcon } from "@/components/ModuleIcon";
import { Sparkles, Loader2, ArrowUpRight, Check, HardHat } from "lucide-react";

const META = Object.fromEntries(MODULES.map((m) => [m.key, m]));
const EMP = ["1-5", "6-20", "21-50", "50+"];
const NEEDS = {
  fr: ["Suivi du temps & présence", "Clients & ventes", "Projets & chantiers", "Comptabilité & paie", "Soumissions & estimation", "Conformité CCQ / Loi 25"],
  en: ["Time & attendance", "Clients & sales", "Projects & job sites", "Accounting & payroll", "Quotes & estimation", "CCQ / Law 25 compliance"],
};
const TIER = { starter: "Starter", pro: "Pro", enterprise: "Entreprise" };

export function AiAdvisor() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState("");
  const [needs, setNeeds] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const L = (fr, en) => (lang === "en" ? en : fr);
  const needOpts = NEEDS[lang] || NEEDS.fr;

  const toggleNeed = (n) => setNeeds((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));

  const run = async () => {
    if (!employees && needs.length === 0 && !message.trim()) {
      setError(L("Indiquez au moins un besoin.", "Select at least one need."));
      return;
    }
    setError(""); setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/advisor/recommend", { employees, needs, message, lang });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || L("Conseiller indisponible.", "Advisor unavailable."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-3xl border border-[#EAB308]/25 bg-[#0a0a0a] p-6 lg:p-8" data-testid="ai-advisor">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#EAB308]/40 bg-[#EAB308]/10">
          <Sparkles className="w-5 h-5 text-[#EAB308]" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">{L("Trouvez vos modules idéaux", "Find your ideal modules")}</h3>
          <p className="text-sm text-white/50">{L("Répondez en 20 secondes, notre IA vous recommande la meilleure combinaison.", "Answer in 20 seconds — our AI recommends your best fit.")}</p>
        </div>
      </div>

      <div className="mt-6 space-y-5">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-white/45">{L("Nombre d'employés", "Number of employees")}</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {EMP.map((e) => (
              <button key={e} onClick={() => setEmployees(e)} data-testid={`adv-emp-${e}`}
                className={`rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${employees === e ? "bg-[#EAB308] text-black" : "bg-white/5 border border-white/12 text-white/70 hover:bg-white/10"}`}>{e}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-white/45">{L("Vos besoins", "Your needs")}</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {needOpts.map((n) => (
              <button key={n} onClick={() => toggleNeed(n)} data-testid={`adv-need-${n.slice(0, 6)}`}
                className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${needs.includes(n) ? "bg-[#dc143c]/20 border border-[#dc143c]/50 text-white" : "bg-white/5 border border-white/12 text-white/70 hover:bg-white/10"}`}>
                {needs.includes(n) && <Check className="mr-1 inline w-3.5 h-3.5 text-[#dc143c]" />}{n}
              </button>
            ))}
          </div>
        </div>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="adv-message" rows={2}
          placeholder={L("Décrivez votre entreprise (optionnel)…", "Describe your company (optional)…")}
          className="w-full rounded-lg border border-white/12 bg-black/40 px-3.5 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#EAB308]/40 focus:outline-none" />
        {error && <p className="text-sm text-red-300" data-testid="adv-error">{error}</p>}
        <button onClick={run} disabled={loading} data-testid="adv-submit"
          className="w-full rounded-lg h-12 font-semibold bg-[#EAB308] text-black hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {L("Analyse…", "Analyzing…")}</> : L("Obtenir ma recommandation", "Get my recommendation")}
        </button>
      </div>

      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 border-t border-white/10 pt-6" data-testid="adv-result">
            {result.intro && <p className="text-white/80">{result.intro}</p>}
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {(result.recommendations || []).map((r) => {
                const m = META[r.module] || {};
                const accent = m.color === "gold" ? "#EAB308" : "#dc143c";
                return (
                  <div key={r.module} className="overflow-hidden rounded-xl border border-white/10 bg-black/40" data-testid={`adv-rec-${r.module}`}>
                    {MODULE_IMAGES[r.module] && <img src={MODULE_IMAGES[r.module]} alt={m.name} className="h-28 w-full object-cover" />}
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ModuleIcon name={m.icon} className="w-4 h-4" style={{ color: accent }} />
                          <span className="font-bold text-white text-sm">{m.name}</span>
                        </div>
                        <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: accent, background: `${accent}18`, border: `1px solid ${accent}33` }}>{TIER[r.tier] || r.tier}</span>
                      </div>
                      <p className="mt-2 text-xs text-white/60 leading-relaxed">{r.why}</p>
                      {m.construction && <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase text-[#f5c77e]"><HardHat className="w-3 h-3" /> {L("Bientôt disponible", "Coming soon")}</span>}
                      <button onClick={() => navigate(`/module/${r.module}`)} className="mt-3 flex items-center gap-1 text-sm font-medium text-[#EAB308] hover:underline">
                        {L("Découvrir", "Discover")} <ArrowUpRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {result.addons?.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-white/60">
                <span>{L("Options :", "Add-ons:")}</span>
                {result.addons.map((a) => <span key={a} className="rounded-full border border-[#EAB308]/30 bg-[#EAB308]/10 px-2.5 py-0.5 text-xs text-[#f3d98a]">{a}</span>)}
              </div>
            )}
            {result.closing && <p className="mt-4 text-white/80 font-medium">{result.closing}</p>}
            <button onClick={() => navigate("/tarification")} data-testid="adv-see-pricing" className="mt-5 rounded-lg bg-[#EAB308] text-black px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
              {L("Voir la tarification", "See pricing")}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
